import { DEFAULT_LOCALE } from './locale.js';
import { domainCopy } from './i18n/index.js';
import { ru } from './i18n/ru.js';
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_RE = /^(\d{4})-(\d{2})$/;
export const WEEKDAYS_RU = ru.calendar.weekdays;
export const MONTHS_RU = ru.calendar.monthsDay;
export const MONTHS_RU_NOM = ru.calendar.monthsTitle;
export function isValidTimeZone(tz) {
    if (typeof tz !== 'string' || tz.length === 0 || tz.length > 64)
        return false;
    try {
        new Intl.DateTimeFormat('en-CA', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
/** нормализует зону: неизвестная/пустая заменяется на UTC */
export function safeTimeZone(tz) {
    return isValidTimeZone(tz) ? tz : 'UTC';
}
/** строгая проверка, что строка — реальная календарная дата YYYY-MM-DD */
export function isDayKey(v) {
    if (typeof v !== 'string')
        return false;
    const m = DAY_RE.exec(v);
    if (!m)
        return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (mo < 1 || mo > 12 || d < 1 || d > 31)
        return false;
    const dt = new Date(Date.UTC(y, mo - 1, d));
    return (dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d);
}
export function isMonthKey(v) {
    if (typeof v !== 'string')
        return false;
    const m = MONTH_RE.exec(v);
    if (!m)
        return false;
    const mo = Number(m[2]);
    return mo >= 1 && mo <= 12;
}
const formatterCache = new Map();
function formatterFor(timeZone) {
    let f = formatterCache.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        formatterCache.set(timeZone, f);
    }
    return f;
}
/**
 * календарный день момента `instant` в зоне `timeZone`.
 * пример: 2026-09-16T17:00:00Z → Asia/Makassar 2026-09-17, UTC 2026-09-16.
 */
export function dayKey(instant, timeZone) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new RangeError('dayKey: instant is not a valid Date');
    }
    if (!isValidTimeZone(timeZone)) {
        throw new RangeError(`dayKey: unknown time zone ${String(timeZone)}`);
    }
    // en-CA даёт ISO-подобный формат YYYY-MM-DD
    return formatterFor(timeZone).format(instant);
}
export function monthKeyOf(day) {
    if (!isDayKey(day))
        throw new RangeError(`monthKeyOf: bad day key ${String(day)}`);
    return day.slice(0, 7);
}
export function monthOf(instant, timeZone) {
    return monthKeyOf(dayKey(instant, timeZone));
}
function parseMonth(month) {
    if (!isMonthKey(month))
        throw new RangeError(`bad month key ${String(month)}`);
    return { y: Number(month.slice(0, 4)), m: Number(month.slice(5, 7)) };
}
export function daysInMonth(month) {
    const { y, m } = parseMonth(month);
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
/** все дни выбранного месяца — целиком, а не остаток (решение владельца, раздел 4) */
export function monthDays(month, locale = DEFAULT_LOCALE) {
    const { y, m } = parseMonth(month);
    const last = daysInMonth(month);
    const out = [];
    for (let d = 1; d <= last; d++) {
        const dt = new Date(Date.UTC(y, m - 1, d));
        const weekday = dt.getUTCDay();
        out.push({
            date: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
            day: d,
            weekday,
            wd: domainCopy(locale).calendar.weekdays[weekday] ?? '',
        });
    }
    return out;
}
export function shiftMonth(month, delta) {
    const { y, m } = parseMonth(month);
    const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
}
export function formatMonth(month, locale = DEFAULT_LOCALE) {
    const { y, m } = parseMonth(month);
    const c = domainCopy(locale).calendar;
    return c.month(c.monthsTitle[m - 1] ?? month, y);
}
export function formatDay(day, locale = DEFAULT_LOCALE) {
    if (!isDayKey(day))
        return day;
    const m = Number(day.slice(5, 7));
    const d = Number(day.slice(8, 10));
    const c = domainCopy(locale).calendar;
    return c.day(d, c.monthsDay[m - 1] ?? '');
}
/** русские имена прежних версий */
export const formatMonthRu = (month) => formatMonth(month, 'ru');
export const formatDayRu = (day) => formatDay(day, 'ru');
/** миллисекунды до следующей местной полуночи — для пересчёта dayKey в UI */
export function msUntilNextLocalMidnight(instant, timeZone) {
    const today = dayKey(instant, timeZone);
    let lo = instant.getTime();
    let hi = lo + 48 * 3600_000;
    // бинарный поиск первой миллисекунды, когда dayKey уже другой
    while (hi - lo > 1) {
        const mid = lo + Math.floor((hi - lo) / 2);
        if (dayKey(new Date(mid), timeZone) === today)
            lo = mid;
        else
            hi = mid;
    }
    return hi - instant.getTime();
}
//# sourceMappingURL=calendar.js.map